import styled from 'styled-components';
import brandConfig from '../../../config/brand';
import { Input } from '../../../components/Input';
import { useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { useRequestLoginMutation } from '../api/authApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authSelector } from '../state/authSlice';
import { toast } from 'react-toastify';
import { useCheckToken } from '../hooks/useCheckToken';
import { useSelector } from 'react-redux';
import { getErrorMessage } from '../../../utils';

export const Login = () => {
  const { token } = useSelector(authSelector);
  const isAuth = Boolean(token);

  const isDev = process.env.NODE_ENV === 'development';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') === '/activate/group/add' ? '/activate/group/add' : '/dashboard';
  const addingSims = redirect === '/activate/group/add';
  const returnFlowAvailable = process.env.REACT_APP_GROUP_RETURNING_CONVERSION_ENABLED === 'true'
    || (process.env.REACT_APP_GROUP_PREVIEW === 'true' && ['localhost', '127.0.0.1'].includes(window.location.hostname));
  const switchAccount = addingSims && searchParams.get('switch') === '1';
  const [linkSent, setLinkSent] = useState(false);
  const [getLogin, { isLoading }] = useRequestLoginMutation();
  const [email, setEmail] = useState(() => redirect === '/activate/group/add'
    ? sessionStorage.getItem('farmstream-group-returning-email') || ''
    : isDev ? 'callum@farmstream.co.uk' : '');

  // If already authenticated, skip the "Go to dashboard" click-through
  useEffect(() => {
    if (isAuth && !switchAccount && (!addingSims || returnFlowAvailable)) {
      navigate(redirect, { replace: true });
    }
  }, [isAuth, navigate, redirect, switchAccount, addingSims, returnFlowAvailable]);

  const onLogin = async () => {
    try {
      const res = await getLogin({ email, ...(redirect !== '/dashboard' ? { redirect } : {}) }).unwrap();

      if (isDev) {
        const token = new URL(res.magicLink).pathname.split('/').pop();
        navigate('/login/' + token + (redirect !== '/dashboard' ? '?redirect=' + encodeURIComponent(redirect) : ''));
        return;
      }

      if (addingSims) {
        setLinkSent(true);
      } else {
        toast(res?.message || 'Login link sent, please check your e-mail', { type: 'success' });
      }
    } catch (err) {
      const errString = getErrorMessage(err, "Couldn't login, please try again");
      toast(errString, { type: 'error' });
    }
  };

  useCheckToken();

  return (
    <Background>
      <StyledImg src={brandConfig.logo} alt={`${brandConfig.displayName} logo`} />
      <Container>
        {addingSims && !returnFlowAvailable ? <SignInIntro>
          <h1>Adding SIMs to an existing plan</h1>
          <p>Our team handles this for now. You do not need to sign in here to request more SIMs.</p>
          <p><a href="mailto:support@farmstream.co.uk?subject=Add%20SIMs%20to%20my%20plan">Ask us to add SIMs →</a></p>
          <Button variant="secondary" onClick={() => navigate('/activate/group')}>← Back to activation</Button>
        </SignInIntro> : !isAuth || switchAccount ? (
          <>
            {addingSims && <SignInIntro>
              <h1>Sign in to add SIMs</h1>
              <p>We’ll send a secure link to the email on your current plan. Open it to return straight to adding SIMs.</p>
            </SignInIntro>}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onLogin();
              }}
            >
              <LoginSection>
                <Input
                  label={addingSims ? 'Email on your current plan' : 'Login to my Streaming account'}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setLinkSent(false); }}
                  placeholder="Enter e-mail"
                />
                <Button type="submit" isLoading={isLoading}>
                  {addingSims ? (linkSent ? 'Resend sign-in link' : 'Email me a sign-in link') : 'Send login link'}
                </Button>
                {addingSims && linkSent && <SignInStatus role="status">
                  Check {email} for your sign-in link. It will take you back to Add SIMs.
                </SignInStatus>}
              </LoginSection>
            </form>
            {addingSims ? <ActivationSection>
              <Button variant="secondary" onClick={() => navigate('/activate/group')}>
                ← Back to activation
              </Button>
            </ActivationSection> : <>
              <Divider>
                <DividerLine />
                <DividerText>or</DividerText>
                <DividerLine />
              </Divider>
              <ActivationSection>
                <Button variant="secondary" onClick={() => navigate('/activate')}>
                  Activate my first streaming SIM
                </Button>
              </ActivationSection>
            </>}
          </>
        ) : (
          <Button onClick={() => navigate('/dashboard')}>Go to dashboard</Button>
        )}
      </Container>
      <BackLink href="https://www.farmstream.co.uk/">
        ← Back to farmstream.co.uk
      </BackLink>
    </Background>
  );
};

const BackLink = styled.a`
  color: ${({ theme }) => theme.colors.white};
  font-size: 0.9rem;
  text-decoration: none;
  opacity: 0.85;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

const StyledImg = styled.img`
  width: 6rem;
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
`;

const Background = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  width: 100%;
  height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem;
`;

const Container = styled.div`
  display: flex;
  max-width: 65rem;
  margin: 0 auto;
  padding: 1rem;
  min-width: 30rem;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 0.5rem;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 768px) {
    min-width: unset;
    max-width: unset;
    width: 100%;
    margin-bottom: 5rem;
  }
`;

const SignInIntro = styled.div`
  color: ${({ theme }) => theme.colors.gray900};
  text-align: center;

  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    line-height: 1.45;
    margin: 0;
  }
`;

const SignInStatus = styled.p`
  color: #126883;
  background: #eff8fb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  line-height: 1.45;
  margin: 0;
`;

const LoginSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  padding-bottom: 0.5rem;
`;

const ActivationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 0.5rem;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0.5rem 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.gray || '#d1d5db'};
`;

const DividerText = styled.span`
  color: ${({ theme }) => theme.colors.gray900};
  font-size: 1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;
